import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { debounce } from "lodash";
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  Button,
  FormHelperText,
  InputAdornment,
  TextField,
  Typography,
  Paper,
  Grid2 as Grid,
  AutocompleteRenderInputParams,
  SxProps,
  Theme,
} from "@mui/material";

import {
  NppesAutocompleteUI,
  IconFaUser,
  NppesAutoComplete,
  IconFaSearch,
  Search,
  NppesAutoCompleteNameInputMargin,
  NppesContainer,
  rowMaxWidth,
  NppesErrorMsgStyle,
  NppesOuterBodyGrid,
  NppesStateSearchWidth,
  SearchNpiContainer,
  DatePickerInput,
} from "../styles/style";
import fontWeight from "../../../styles/mui/fontWeight";
import { colors } from "../../../styles/colors";
import useMenuItem from "../../../hooks/useMenuItem";
import { getValue, isNil, stateListData, length } from "../../../utils";
import {
  NppesFadedContainedButtonUI,
  NppesNPIPadding,
  NppesNPISearchPadding,
} from "../../../styles/common/style";
import { NppesSearch } from "../../../models/PhysicianDetails";
import {
  flexAlignCentre,
  flexAlignEnd,
  flexAllCentre,
} from "../../../styles/mui/styles/display";
import VirtualizeAutocomplete from "../../FormPriorAuthReAuth/AutocompleteList";
import { NPPESDataResponse } from "../../../models/api/PhysicianDetails";
import {
  AvailableRecords,
  NppesAutoCompletePaper,
  StyledPopper,
  StyledPopperCheckBoxAutocomplete,
} from "../../styles/styles";
import {
  getTableBody,
  getTableHead,
  TableDropdown,
} from "../../../constants/AllPatientRecord";
import {
  GetNPPESDataActionDispatchTypes,
  getNppesTypeDetails,
  NppesApiCall,
  nppesInitialRecord,
  NPPESinitialRecord,
  nppesOrganizationErrorMsg,
  nppesPhysicianErrorMsg,
  NppesType,
} from "../../../constants/PhysicianDetails";
import { stateNameAbbreviations } from "../../../constants/StateAbbreviations";
import { NO_RECORD_FOUND } from "../../../constants/eligibility";
import { ButtonMinWidth } from "../../../routes/PriorAuthWorkFlow/styles/style";
import { Numbers } from "../../../constants/Referral";

export interface PropsFromState {
  NppesOpen: boolean;
  NppesSetOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelect: React.Dispatch<React.SetStateAction<any>>;
  PhysicianDetailsData: NPPESDataResponse;
  openAutocomplete: boolean;
  setOpenAutocomplete: React.Dispatch<React.SetStateAction<boolean>>;
  nppesSearchInjectedto?: string;
  type: string;
}

export interface PropsFromDispatch {
  resetStates?: (actionType: string) => void;
  getNPPESDetails: (payload: any) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const NppesSearchComponent: React.FC<AllProps> = ({
  NppesOpen,
  NppesSetOpen,
  handleSelect,
  PhysicianDetailsData,
  getNPPESDetails,
  resetStates,
  openAutocomplete,
  setOpenAutocomplete,
  nppesSearchInjectedto,
  type
}: AllProps) => {
  const { open, onOpen, onClose } = useMenuItem();
  const [requestorType, setRequestorType] = useState<string>("");
  const [stateList, setStateList] = useState<any[]>([]);
  const [stateName, setStateName] = useState<any>();
  const [payloadStateName, setPayloadStateName] = useState<any>(null);
  const [lastNameinputValue, setLastNameInputValue] = useState<string>("");
  const [firstNameInputValue, setFirstNameInputValue] = useState<string>("");
  const [physicianListByNameResp, setPhysicianListByNameResp] = useState<
    NppesSearch[]
    >([]);
  const [physicianListByNPIResp, setPhysicianListByNPIResp] = useState<
    NppesSearch[]
  >([]);
  const [apicallIdentifier, setApiCallIdentifier] = useState<string>("");
  const { response, loading } = PhysicianDetailsData;
  const [npiNumberLength, setNpiNumberLength] = useState<string>("");
  const autocompleteRef = useRef<HTMLInputElement | null>(null);
  const BtnRef = useRef<HTMLButtonElement | null>(null);
  const lastNameFieldRef = useRef<HTMLInputElement | null>(null);
  const firstNameFieldRef = useRef<HTMLInputElement | null>(null);
  const [typeDetails, setTypeDetails] = useState<any>(getNppesTypeDetails(type));

  const methods = useForm<any>({
    defaultValues: null,
    mode: "all",
  });

  const { control, handleSubmit} = methods;

  const debouncedSearch = debounce((event, value, reason) => {
    handleNppesName(event, value, reason);
  }, 500);

  useEffect(() => {
    if (getValue(response, "[0].name", "") !== "") {
      if (
        apicallIdentifier === NppesApiCall.BY_NAME &&
        physicianListByNameResp.length === 0
      ) {
        setPhysicianListByNPIResp([]);
        setPhysicianListByNameResp([...nppesInitialRecord, ...response]);
      } else if (apicallIdentifier === NppesApiCall.BY_NPI) {
        setPhysicianListByNameResp([]);
        setPhysicianListByNPIResp([...response]);
      }
    } else {
      setPhysicianListByNameResp(() => []);
      setPhysicianListByNPIResp(() => []);
    }
  }, [response]);

  const removeEventListener = () => {
    window.removeEventListener("scroll", closeDropdown);
    document.removeEventListener("mousedown", closeMuiDropdown);
  };

  useEffect(() => {
    if (openAutocomplete) {
      window.addEventListener("scroll", closeDropdown);
      document.addEventListener("mousedown", closeMuiDropdown);
    } else {
      removeEventListener();
    }
    return removeEventListener;
  }, [openAutocomplete]);

  useEffect(() => {
    if (npiNumberLength.length < 10) {
      NppesSetOpen && NppesSetOpen(false);
      setPhysicianListByNPIResp(() => []);
      setOpenAutocomplete(false);
    }
  }, [npiNumberLength]);

  const handleFocus = () => {
    if (npiNumberLength.length === 10) {
      setApiCallIdentifier(NppesApiCall.BY_NPI);
      NppesSetOpen && NppesSetOpen(true);
      callNPPESDetails(npiNumberLength);
    } else {
      NppesSetOpen && NppesSetOpen(false);
    }
  };

  const callNPPESDetails = (value: string) => {
    if (requestorType === NppesType.PhysicianOffice) {
      getNPPESDetails({
        npi: value,
        type: Numbers.ONE,
      });
    } else {
      getNPPESDetails({
        npi: value,
        type: Numbers.TWO,
      });
    }
  }

  const refs = [autocompleteRef, BtnRef, firstNameFieldRef, lastNameFieldRef];

  const closeMuiDropdown = (event: MouseEvent) => {
    setTimeout(() => {
      const isOutsideClick = refs.every(
        (ref) => {
          if (!ref.current) return true;
          return !ref.current.contains(event.target as Node);
        }
      );

      if (isOutsideClick) {
        setOpenAutocomplete(false);
        setPhysicianListByNameResp([]);
      }
    }, 0);
  };

  const closeDropdown = () => {
    setOpenAutocomplete(false);
    setPhysicianListByNameResp(() => []);
    setPhysicianListByNPIResp(() => []);
  };

  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setPhysicianListByNameResp(() => []);
    debouncedSearch(event, value, reason);
    setNpiNumberLength(value.trim());
  };

  const handleNppesName = (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setApiCallIdentifier("");
    setPhysicianListByNameResp([]);
    if (!isNil(value) && !isNil(event)) {
      if (value.length < 10) {
        NppesSetOpen && NppesSetOpen(false);
      }
      if (value.trim().length === 10 && reason === "input") {
        setApiCallIdentifier(NppesApiCall.BY_NPI);
        setPhysicianListByNameResp([]);
        resetStates &&
          resetStates(GetNPPESDataActionDispatchTypes.GET_NPPES_DATA_RESET);
        NppesSetOpen && NppesSetOpen(true);
        callNPPESDetails(value);
      }
    }
  };

 const validateNPPESNames = () => {
    const nameFilled = lastNameinputValue && lastNameinputValue.trim() !== "";
   if (requestorType === NppesType.PhysicianOffice) {
      const firstNameFilled =
        firstNameInputValue && firstNameInputValue.trim() !== "";
      return firstNameFilled || nameFilled || nppesPhysicianErrorMsg;
    }
    return nameFilled || nppesOrganizationErrorMsg;
  };

  useEffect(() => {
    const statesArray = stateListData();
    setStateList(statesArray);
  }, [stateNameAbbreviations]);

  const defaultProps = (stateList: any[] | null | undefined) => ({
    options: stateList as any[],
    getOptionLabel: (option: any) => option.stateName,
  });

  const onSelection = (
    _e: SyntheticEvent<Element, Event>,
    value: any | null
  ) => {
    setStateName(value);
    const stateNameData =
      value != null && !isNil(stateList)
        ? stateList &&
          stateList.find(
            (v: any) =>
              v.stateName === value.stateName && v.stateId === value.stateId
          )
        : null;
    setPayloadStateName(stateNameData === null ? null : stateNameData.stateId);
    onClose();
  };

  const handleChoose = (option?: NppesSearch) => {
    handleSelect(option);
    setOpenAutocomplete(false);
    setPhysicianListByNameResp(() => []);
    setPhysicianListByNPIResp(() => []);
    setLastNameInputValue("");
    setFirstNameInputValue("");
    setStateName(null);
    setPayloadStateName(null);
  };

  const onSubmit = () => {
    setOpenAutocomplete(true);
    setApiCallIdentifier(NppesApiCall.BY_NAME);
    resetStates &&
      resetStates(GetNPPESDataActionDispatchTypes.GET_NPPES_DATA_RESET);
    if (requestorType === NppesType.PhysicianOffice) {
      getNPPESDetails({
        firstName: firstNameInputValue && firstNameInputValue + "*",
        lastName: lastNameinputValue && lastNameinputValue + "*",
        state: payloadStateName,
        type: Numbers.ONE,
      });
    } else {
      getNPPESDetails({
        organizationName: lastNameinputValue && lastNameinputValue + "*",
        state: payloadStateName,
        type: Numbers.TWO,
      });
    }
  };

  useEffect(() => {
    setRequestorType(type); 
    setTypeDetails(getNppesTypeDetails(type));
  },[type])

  const handleSearch = () => {
    setPhysicianListByNPIResp([]);
    handleSubmit(onSubmit)();
  };

  const CustomPaper = (props: any) => {
    return (
      <Paper
        {...props}
        sx={NppesAutoCompletePaper("55rem")}
        ref={autocompleteRef}
      />
    );
  };

  const header = (position: any, headerTitle: string, sx: SxProps<Theme>) => {
    return (
      <Box textAlign={position} sx={sx}>
        <Typography
          variant="subtitle2"
          color={colors.black[14]}
          fontWeight={fontWeight[5]}
        >
          {headerTitle}
        </Typography>
      </Box>
    );
  };

  const body = (
    value: any,
    isButton: boolean,
    sx: SxProps<Theme>,
    option?: NppesSearch
  ) => {
    return (
      <Box sx={sx}>
        {isButton === false ? (
          <Typography
            variant="subtitle2"
            color={colors.black[4]}
            fontWeight={fontWeight[4]}
          >
            {value}
          </Typography>
        ) : (
          <Button
            variant="contained"
            sx={[NppesFadedContainedButtonUI]}
            onClick={() => handleChoose(option)}
            ref={BtnRef}
            data-testid={`${nppesSearchInjectedto}-nppes-choose`}
          >
            CHOOSE
          </Button>
        )}
      </Box>
    );
  };

  const filterOptions = (options: any, inputValue: string) => {
    const lowercasedInput = inputValue?.toLowerCase() || "";
    return options.filter(
      (option: any) =>
        option.stateName.toLowerCase().includes(lowercasedInput) ||
        option.stateId.toLowerCase().includes(lowercasedInput)
    );
  };
  
  const nppesOption = (option: any) => {
    return (
      <Box sx={rowMaxWidth} key={option.npiId}>
        {option.npiId === "" && (
          <Box sx={AvailableRecords}>
            <Typography
              variant="subtitle2"
              color={colors.black[2]}
              fontWeight={fontWeight[4]}
            >
              Available records :
            </Typography>
          </Box>
        )}
        {option.npiId === "" ? (
          <Box sx={NppesContainer.header.row}>
            {header("left", "Name:", NppesContainer.header.cell1)}
            {header("left", "NPI Number:", NppesContainer.header.cell2)}
            {header("left", "Address:", NppesContainer.header.cell3)}
            {header("left", "Action:", NppesContainer.header.cell4)}
          </Box>
        ) : (
          <Box sx={NppesContainer.body.row}>
            {body(option.name, false, NppesContainer.body.cell1)}
            {body(option.npiId, false, NppesContainer.body.cell2)}
            {body(option.address, false, NppesContainer.body.cell3)}
            {body("", true, NppesContainer.body.cell4, option)}
          </Box>
        )}
      </Box>
    );
  };
  
  const nppsAutocompleteTextField = (params: any) => {
    return (
      <Box sx={Search}>
        <TextField
          {...params}
          variant="standard"
          placeholder="Choose State(Optional)"
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <IconFaSearch />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
            },
          }}
          sx={DatePickerInput}
        />
      </Box>
    );
  };

  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>
        <Grid container sx={NppesOuterBodyGrid}>
          <Grid size={{ xs: 4 }} sx={flexAlignEnd}>
            <Grid container sx={SearchNpiContainer} pb={1.5}>
              <Grid size={{ xs: 12 }} pb={1.5}>
                <Typography
                  variant="subtitle2"
                  fontWeight={fontWeight[5]}
                  color={colors.fonts[4]}
                  sx={NppesNPISearchPadding}
                  display="inline"
                >
                  Search by NPI
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontWeight={fontWeight[2]}
                  color={colors.black[2]}
                  sx={NppesNPIPadding}
                  display="inline"
                >
                  (Requires full NPI number to view the results):
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Controller
                    name={"npi"}
                    control={control}
                    defaultValue={null}
                    render={({ fieldState: { error } }) => {
                      return (
                        <>
                          <VirtualizeAutocomplete
                            options={physicianListByNPIResp}
                            value={NPPESinitialRecord}
                            handleInputChange={handleInputChange}
                            sx={NppesAutocompleteUI}
                            open={NppesOpen}
                            handleSelect={handleSelect}
                            tableHead={getTableHead(TableDropdown.PHYSICIAN)}
                            tableBody={getTableBody(TableDropdown.PHYSICIAN)}
                            autocompleteInputIcon={<IconFaUser />}
                            setOpen={NppesSetOpen}
                            maxLength={10}
                            width="55rem"
                            loading={loading}
                            placeholder="Enter NPI number"
                            InputType="onlyNumber"
                            handleFocus={handleFocus}
                            hideCrossIcon={true}
                            setClearBlur={true}
                            injectedTo={`${nppesSearchInjectedto}-search-by-npi`}
                          />
                          {error && (
                            <FormHelperText>{error.message}</FormHelperText>
                          )}
                        </>
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 0.5 }} sx={flexAlignCentre}>
            <Grid container sx={flexAllCentre}>
              <Typography
                variant="subtitle2"
                fontWeight={fontWeight[5]}
                color={colors.fonts[4]}
              >
                OR
              </Typography>
            </Grid>
          </Grid>
          <Grid size={{ xs: 7.5 }} sx={flexAlignEnd}>
            <Grid container sx={{ width: "100%" }}>
              <Grid size={{ xs: 12 }} pb={1.5}>
                <Typography
                  variant="subtitle2"
                  fontWeight={fontWeight[5]}
                  color={colors.fonts[4]}
                >
                 {typeDetails.title}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box height={"2.859rem"}>
                  <Grid container gap={"0.5rem"}>
                      <Grid size={{ xs: 2.5 }}>
                      <Box sx={NppesAutoCompleteNameInputMargin}>
                        <Controller
                          name={typeDetails.name}
                          control={control}
                          defaultValue={null}
                          rules={{ validate: () => validateNPPESNames() }}
                          render={({ fieldState: { error } }) => {
                            return (
                              <>
                                <Autocomplete
                                  options={physicianListByNameResp}
                                  data-testid={`${nppesSearchInjectedto}-nppes-last-Name`}
                                  value={null}
                                  clearIcon={null}
                                  freeSolo
                                  getOptionLabel={(option: any) =>
                                    option.physicianName || ""
                                  }
                                  slots={{
                                    paper: CustomPaper,
                                    popper: StyledPopper,
                                  }}
                                  loading={loading}
                                  filterOptions={(options) => options}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="standard"
                                      placeholder={typeDetails.placeHolder}
                                      slotProps={{
                                        input: {
                                          ...params.InputProps,
                                        },
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.stopPropagation();
                                        }
                                      }}
                                      inputRef={lastNameFieldRef}
                                    />
                                  )}
                                  renderOption={(_props, option) => {
                                    return nppesOption(option);
                                  }}
                                  sx={DatePickerInput}
                                  inputValue={lastNameinputValue}
                                  onInputChange={(_event, newInputValue) =>
                                    setLastNameInputValue(newInputValue)
                                  }
                                  onOpen={() => {
                                    if (
                                      Array.isArray(physicianListByNameResp) &&
                                      length(physicianListByNameResp)
                                    ) {
                                      setOpenAutocomplete(true);
                                    }
                                  }}
                                  open={openAutocomplete}
                                  fullWidth
                                />
                                {error && (
                                  <FormHelperText sx={NppesErrorMsgStyle}>
                                    {error.message}
                                  </FormHelperText>
                                )}
                              </>
                            );
                          }}
                        />
                      </Box>
                    </Grid>
                     {type === NppesType.PhysicianOffice && (<>
                    <Grid size={{ xs: 2.5 }}>
                      <Box sx={NppesAutoCompleteNameInputMargin}>
                        <Controller
                          name={"firstName"}
                          rules={{ validate: validateNPPESNames }}
                          control={control}
                          defaultValue={null}
                          render={() => {
                            return (
                              <>
                                <Autocomplete
                                  options={[]}
                                  value={null}
                                  freeSolo
                                  getOptionLabel={(option: any) =>
                                    option.physicianName || ""
                                  }
                                  data-testid={`${nppesSearchInjectedto}-nppes-first-Name`}
                                  slots={{
                                    paper: CustomPaper,
                                    popper: StyledPopper,
                                  }}
                                  clearIcon={null}
                                  renderInput={(
                                    params: AutocompleteRenderInputParams
                                  ) => (
                                    <TextField
                                      {...params}
                                      variant="standard"
                                      placeholder="Enter First Name"
                                      slotProps={{
                                        input: {
                                          ...params.InputProps,
                                        },
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.stopPropagation();
                                        }
                                      }}
                                      inputRef={firstNameFieldRef}
                                    />
                                  )}
                                  sx={DatePickerInput}
                                  inputValue={firstNameInputValue}
                                  onInputChange={(_event, newInputValue) =>
                                    setFirstNameInputValue(newInputValue)
                                  }
                                  onOpen={() => {
                                    if (
                                      Array.isArray(physicianListByNameResp) &&
                                      length(physicianListByNameResp)
                                    ) {
                                      setOpenAutocomplete(true);
                                    }
                                  }}
                                  fullWidth
                                />
                              </>
                            );
                          }}
                        />
                      </Box>
                    </Grid>
                    </>)}
                    
                    <Grid size={{ xs: 4 }} sx={NppesStateSearchWidth}>
                      <Box sx={NppesAutoCompleteNameInputMargin}>
                        <Controller
                          name={"state"}
                          control={control}
                          defaultValue={null}
                          render={() => {
                            return (
                              <>
                                <Autocomplete
                                  sx={NppesAutoComplete}
                                  data-testid={`${nppesSearchInjectedto}-nppes-state`}
                                  {...defaultProps(stateList)}
                                  id="auto-complete"
                                  autoComplete
                                  onChange={(e, v) => {
                                    onSelection(e, v);
                                  }}
                                  clearIcon={null}
                                  includeInputInList
                                  value={stateName || null}
                                  noOptionsText={NO_RECORD_FOUND}
                                  slots={{
                                    paper: CustomPaper,
                                    popper: StyledPopperCheckBoxAutocomplete,
                                  }}
                                  disabled={isNil(stateList) ? true : false}
                                  isOptionEqualToValue={(option, value) =>
                                    option.stateId === value.stateId &&
                                    option.stateName === value.stateName
                                  }
                                  filterOptions={(options, { inputValue }) => {
                                    return filterOptions(options, inputValue);
                                  }}
                                  onOpen={onOpen}
                                  onClose={onClose}
                                  clearOnBlur={true}
                                  renderInput={(params) => {
                                    params.inputProps.maxLength = 50;
                                    return nppsAutocompleteTextField(params);
                                  }}
                                  onInputChange={(_event, value) =>
                                    value !== "" && onOpen
                                  }
                                  open={open}
                                  renderOption={(props, option) => {
                                    return (
                                      <li {...props} key={option.stateId}>
                                        {option.stateName}
                                      </li>
                                    );
                                  }}
                                />
                              </>
                            );
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Box sx={NppesAutoCompleteNameInputMargin}>
                        <Button
                          onClick={handleSearch}
                          variant="contained"
                          sx={ButtonMinWidth}
                          data-testid={`${nppesSearchInjectedto}-nppes-search`}
                        >
                          SEARCH
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
            <Grid container></Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default NppesSearchComponent;
